import { useEffect, useState } from "react";
import { Button, Input, Modal, Select } from "@legal-500/legal500-design-system";
import {
  AI_PARSE_BULK_RERUN_LIMIT,
  aiParser,
  type AiParseBulkRerunEstimate,
  type AiParseBulkRerunInput,
  type AiParseBulkRerunResponse,
  type AiSchema,
} from "@/api/aiParser.service";
import styles from "./AiParseRuns.module.css";

type Selection = "lastN" | "range";

type FormState = {
  aiSchemaId: string;
  selection: Selection;
  createdFrom: string;
  createdTo: string;
  lastNSubmissions: string;
  limit: string;
  skipAlreadyRun: boolean;
};

type FieldErrors = Partial<Record<keyof FormState, string>>;

/**
 * `skipAlreadyRun` starts ON here while the API defaults it OFF, and that difference is deliberate.
 * The API's default is the conservative one for any caller that asked for a rerun and meant it.
 * This modal exists for the backfill: thousands of pre-existing submissions, run in chunks, with
 * overlapping selections chosen as people go — so a document that already has a result is money
 * spent for nothing. The value is sent explicitly on every request, so it is this default that
 * applies rather than the server's.
 */
function emptyForm(): FormState {
  return {
    aiSchemaId: "",
    selection: "lastN",
    createdFrom: "",
    createdTo: "",
    lastNSubmissions: "25",
    limit: String(AI_PARSE_BULK_RERUN_LIMIT),
    skipAlreadyRun: true,
  };
}

/** An HTML date input gives YYYY-MM-DD; the API wants ISO 8601 carrying an offset. */
function toIsoDate(value: string, endOfDay: boolean): string | undefined {
  if (!value) return undefined;
  return endOfDay ? `${value}T23:59:59.999Z` : `${value}T00:00:00.000Z`;
}

/**
 * A job total, not a per-run cost, so two decimal places read better — except when that would
 * round a real spend down to $0.00, which is the one thing this modal must never print.
 */
function formatUsd(value: string): string {
  const parsed = Number(value);
  if (Number.isNaN(parsed)) return value;
  return `$${parsed >= 0.01 ? parsed.toFixed(2) : parsed.toFixed(4)}`;
}

function validate(form: FormState): { errors: FieldErrors; input: AiParseBulkRerunInput | null } {
  const errors: FieldErrors = {};

  if (!form.aiSchemaId) errors.aiSchemaId = "Choose the schema to rerun";

  let lastNSubmissions: number | undefined;
  let limit: number | undefined;

  if (form.selection === "lastN") {
    const parsed = Number(form.lastNSubmissions);
    if (!form.lastNSubmissions.trim() || Number.isNaN(parsed) || !Number.isInteger(parsed)) {
      errors.lastNSubmissions = "Enter a whole number of submissions";
    } else if (parsed < 1) {
      errors.lastNSubmissions = "Enter at least 1 submission";
    } else if (parsed > AI_PARSE_BULK_RERUN_LIMIT) {
      // The API rejects an over-large number rather than trimming it, so asking for 1,000 would
      // otherwise get nothing done at all.
      errors.lastNSubmissions = `One job covers at most ${AI_PARSE_BULK_RERUN_LIMIT} documents`;
    } else {
      lastNSubmissions = parsed;
    }
  } else {
    if (!form.createdFrom && !form.createdTo) {
      errors.createdFrom = "Give a from date, a to date, or both";
    } else if (form.createdFrom && form.createdTo && form.createdFrom > form.createdTo) {
      errors.createdTo = "The to date must not be before the from date";
    }

    const parsed = Number(form.limit);
    if (!form.limit.trim() || Number.isNaN(parsed) || !Number.isInteger(parsed)) {
      errors.limit = "Enter a whole number of documents";
    } else if (parsed < 1) {
      errors.limit = "Enter at least 1 document";
    } else if (parsed > AI_PARSE_BULK_RERUN_LIMIT) {
      errors.limit = `One job covers at most ${AI_PARSE_BULK_RERUN_LIMIT} documents`;
    } else {
      limit = parsed;
    }
  }

  if (Object.keys(errors).length > 0) return { errors, input: null };

  return {
    errors,
    input: {
      aiSchemaId: form.aiSchemaId,
      // Overwritten per call; declared here so the safe value is never the one you get by
      // forgetting a field.
      dryRun: true,
      // Always sent, never left to the server's default — see emptyForm.
      skipAlreadyRun: form.skipAlreadyRun,
      ...(form.selection === "lastN"
        ? { lastNSubmissions }
        : {
            limit,
            createdFrom: toIsoDate(form.createdFrom, false),
            createdTo: toIsoDate(form.createdTo, true),
          }),
    },
  };
}

function EstimateRow({ label, value }: { label: string; value: string }) {
  return (
    <div className={styles.estimateRow}>
      <dt className={styles.estimateLabel}>{label}</dt>
      <dd className={styles.estimateValue}>{value}</dd>
    </div>
  );
}

function estimateFootnote(estimate: AiParseBulkRerunEstimate): string {
  if (estimate.runsSampled === 0) {
    return "This schema has never run, so there is no history to estimate from. The API will not invent a figure — the only way to find out what this costs is to run it.";
  }
  return `Projected from what this schema's last ${estimate.runsSampled} run${
    estimate.runsSampled === 1 ? "" : "s"
  } actually used. It is an average, not a quote.`;
}

type Props = {
  isOpen: boolean;
  schemas: AiSchema[];
  onClose: () => void;
  /** Called after a real enqueue, so the page can pick up whatever the workers have started. */
  onEnqueued: () => void;
};

export function BulkRerunModal({ isOpen, schemas, onClose, onEnqueued }: Props) {
  const [form, setForm] = useState<FormState>(emptyForm);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [estimate, setEstimate] = useState<AiParseBulkRerunResponse | null>(null);
  const [acknowledged, setAcknowledged] = useState(false);
  const [result, setResult] = useState<AiParseBulkRerunResponse | null>(null);
  const [pending, setPending] = useState<"idle" | "checking" | "enqueueing">("idle");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setForm(emptyForm());
    setErrors({});
    setEstimate(null);
    setAcknowledged(false);
    setResult(null);
    setPending("idle");
    setError(null);
  }, [isOpen]);

  /**
   * Any edit throws the estimate away. The confirm button is a promise that this many documents
   * cost about this much — leaving a stale estimate above a changed selection would break it.
   */
  function update(patch: Partial<FormState>) {
    setForm(current => ({ ...current, ...patch }));
    setEstimate(null);
    setAcknowledged(false);
    setError(null);
  }

  async function submit(dryRun: boolean) {
    const { errors: found, input } = validate(form);
    setErrors(found);
    if (!input) return;

    setPending(dryRun ? "checking" : "enqueueing");
    setError(null);
    try {
      const response = await aiParser.bulkRerun({ ...input, dryRun });
      if (dryRun) {
        setEstimate(response);
        setAcknowledged(false);
      } else {
        setResult(response);
        onEnqueued();
      }
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught));
    } finally {
      setPending("idle");
    }
  }

  const schemaName = schemas.find(schema => schema.id === form.aiSchemaId)?.name ?? "this schema";

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Bulk rerun a schema" size="mediumLarge">
      {result ? (
        <div className={styles.modalBody}>
          <p className={styles.confirmText}>
            Queued <strong>{result.enqueued}</strong> rerun{result.enqueued === 1 ? "" : "s"} of{" "}
            {schemaName}
            {result.skipped > 0 ? `, and skipped ${result.skipped}` : ""}. They appear in the table
            as their own rows as the workers pick them up.
          </p>
          {result.capped && (
            <p className={styles.warnNote}>
              {result.total} documents matched but one job covers at most {result.cap}. Run it again
              for the rest.
            </p>
          )}
          <div className={styles.formActions}>
            <Button variant="contained" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      ) : (
        <div className={styles.modalBody}>
          <p className={styles.confirmText}>
            Runs one schema over many documents, as new runs. Nothing is queued until you have seen
            the candidate count and the estimated cost and confirmed them.
          </p>

          <div className={styles.modalFields}>
            <Select
              label="Schema"
              fullWidth
              value={form.aiSchemaId}
              error={errors.aiSchemaId}
              onChange={event => update({ aiSchemaId: event.target.value })}
              options={[
                { value: "", label: "Choose a schema" },
                ...schemas.map(schema => ({
                  value: schema.id,
                  label: schema.enabled ? schema.name : `${schema.name} (disabled)`,
                })),
              ]}
            />

            <Select
              label="Select documents by"
              fullWidth
              value={form.selection}
              onChange={event => update({ selection: event.target.value as Selection })}
              options={[
                { value: "lastN", label: "Last N submissions" },
                { value: "range", label: "Date range" },
              ]}
            />

            {form.selection === "lastN" ? (
              <Input
                label="Number of submissions"
                type="number"
                fullWidth
                min={1}
                max={AI_PARSE_BULK_RERUN_LIMIT}
                value={form.lastNSubmissions}
                error={errors.lastNSubmissions}
                helperText={`The most recent submissions, newest first. At most ${AI_PARSE_BULK_RERUN_LIMIT}.`}
                onChange={event => update({ lastNSubmissions: event.target.value })}
              />
            ) : (
              <>
                <Input
                  label="Submitted from"
                  type="date"
                  fullWidth
                  value={form.createdFrom}
                  error={errors.createdFrom}
                  onChange={event => update({ createdFrom: event.target.value })}
                />
                <Input
                  label="Submitted to"
                  type="date"
                  fullWidth
                  value={form.createdTo}
                  error={errors.createdTo}
                  onChange={event => update({ createdTo: event.target.value })}
                />
                <Input
                  label="Maximum documents"
                  type="number"
                  fullWidth
                  min={1}
                  max={AI_PARSE_BULK_RERUN_LIMIT}
                  value={form.limit}
                  error={errors.limit}
                  helperText={`One job is hard-capped at ${AI_PARSE_BULK_RERUN_LIMIT} documents.`}
                  onChange={event => update({ limit: event.target.value })}
                />
              </>
            )}

            <label className={styles.checkboxRow}>
              <input
                type="checkbox"
                checked={form.skipAlreadyRun}
                onChange={event => update({ skipAlreadyRun: event.target.checked })}
              />
              <span>
                <strong>Skip documents this schema has already run against</strong> — a document
                counts as done once it has a succeeded or invalid run for this schema. Toggling this
                changes what the job costs, so it throws the impact check away: check it again.
              </span>
            </label>
          </div>

          {error && (
            <div className={styles.errorBanner} role="alert">
              {error}
            </div>
          )}

          {estimate && (
            <div className={styles.estimatePanel}>
              <h4 className={styles.estimateTitle}>Before anything is queued</h4>
              <dl className={styles.estimateGrid}>
                <div className={styles.estimateRow}>
                  <dt className={styles.estimateLabel}>Documents to rerun</dt>
                  <dd className={styles.estimateHeadline}>{estimate.candidates}</dd>
                </div>
                <div className={styles.estimateRow}>
                  <dt className={styles.estimateLabel}>Estimated cost</dt>
                  <dd
                    className={
                      estimate.estimate.estimatedCostUsd === null
                        ? styles.estimateUnknown
                        : styles.estimateHeadline
                    }
                  >
                    {estimate.estimate.estimatedCostUsd === null
                      ? "No history yet — cost unknown"
                      : formatUsd(estimate.estimate.estimatedCostUsd)}
                  </dd>
                </div>
                <EstimateRow label="Matching this selection" value={String(estimate.total)} />
                <EstimateRow
                  label="Already run by this schema"
                  value={String(estimate.alreadyRun)}
                />
                <EstimateRow
                  label="Estimated tokens"
                  value={
                    estimate.estimate.estimatedTotalTokens === null
                      ? "unknown"
                      : estimate.estimate.estimatedTotalTokens.toLocaleString()
                  }
                />
              </dl>
              <p className={styles.estimateFootnote}>{estimateFootnote(estimate.estimate)}</p>

              {/*
                Matching this selection and Documents to rerun differing is the thing most likely to
                be misread as a bug, so whichever way the skip is set, the gap is spelled out.
              */}
              {estimate.alreadyRun > 0 &&
                (form.skipAlreadyRun ? (
                  <p className={styles.estimateFootnote} role="status">
                    {`Skipping ${estimate.alreadyRun} of the ${estimate.total} documents this selection matches — ${schemaName} has already run against ${estimate.alreadyRun === 1 ? "it" : "them"}. Documents to rerun is what is left after the skip, and then after the ${estimate.cap}-per-job cap.`}
                  </p>
                ) : (
                  <p className={styles.warnNote} role="status">
                    {`${estimate.alreadyRun} of the ${estimate.total} documents this selection matches have already been run by ${schemaName}. Skipping is switched off, so they will be run — and paid for — a second time.`}
                  </p>
                ))}

              {estimate.capped && (
                <p className={styles.warnNote} role="status">
                  {estimate.total} documents match but one job covers at most {estimate.cap}. Only
                  the first {estimate.cap} will be rerun — run it again for the rest.
                </p>
              )}

              {estimate.candidates === 0 ? (
                <p className={styles.confirmText}>Nothing matches this selection.</p>
              ) : (
                <label className={styles.ackRow}>
                  <input
                    type="checkbox"
                    checked={acknowledged}
                    onChange={event => setAcknowledged(event.target.checked)}
                  />
                  <span>
                    I understand this queues {estimate.candidates} paid model call
                    {estimate.candidates === 1 ? "" : "s"} against {schemaName}
                    {estimate.estimate.estimatedCostUsd === null
                      ? ", and that nobody can say yet what they will cost"
                      : ""}
                    .
                  </span>
                </label>
              )}
            </div>
          )}

          <div className={styles.formActions}>
            <Button variant="outlined" onClick={onClose} disabled={pending !== "idle"}>
              Cancel
            </Button>
            <Button variant="outlined" onClick={() => submit(true)} disabled={pending !== "idle"}>
              {pending === "checking"
                ? "Checking…"
                : estimate
                  ? "Re-check impact"
                  : "Check impact (nothing is queued)"}
            </Button>
            {estimate && estimate.candidates > 0 && (
              <Button
                variant="contained"
                color="error"
                disabled={!acknowledged || pending !== "idle"}
                onClick={() => submit(false)}
              >
                {pending === "enqueueing"
                  ? "Queueing…"
                  : `Enqueue ${estimate.candidates} rerun${estimate.candidates === 1 ? "" : "s"}`}
              </Button>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}
